// Users (wave 3): the tab host for Customers, Drivers, Fleet admins,
// Internal staff and All admins, with the theme's tabs (the selected tab in
// orange words over a 2-px orange line) instead of a per-view style.
//
// Access, exactly as before (common/tabSecurity): only the tabs the role may
// open are drawn; the URL /users/:id is checked with validateTabIndex and an
// unknown or forbidden tab goes to the role's default tab; every change is
// logged with logTabAccess. Only the open tab is mounted (the others unmount,
// as before).
import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Riders from './Riders';
import Drivers from './Drivers';
import FleetAdmins from './FleetAdmins';
import CreateAdmin from './CreateAdmin';
import InternalStaff from './InternalStaff';
import { tokens as c } from '../theme';
import { EmptyState } from '../components/feedback';
import { calmCaps } from '../components/modern';
import { TAB_CONFIG, validateTabIndex, getDefaultTabIndex, canAccessTab, logTabAccess, getVisibleTabs } from '../common/tabSecurity';

export default function Users() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const role = auth.profile && auth.profile.usertype ? auth.profile.usertype : null;
  // Tabs user is authorized to see
  const authorizedTabs = React.useMemo(() => (role ? getVisibleTabs(role) : []), [role]);

  const handleChange = (event, newValue) => {
    // Validate access before navigation
    if (canAccessTab(newValue, role)) {
      logTabAccess(role, newValue, true);
      navigate(`/users/${newValue}`);
    } else {
      logTabAccess(role, newValue, false);
      // Redirect to default tab if unauthorized
      const defaultTab = getDefaultTabIndex(role);
      navigate(`/users/${defaultTab}`);
    }
  };

  React.useEffect(() => {
    if (!role) return; // Wait for role to be set

    if (id !== undefined) {
      // Validate the tab index from URL
      const validatedIndex = validateTabIndex(id, role);

      if (validatedIndex !== null) {
        setValue(validatedIndex);
        logTabAccess(role, validatedIndex, true);
      } else {
        // Invalid or unauthorized tab - redirect to default
        const defaultTab = getDefaultTabIndex(role);
        logTabAccess(role, id, false);
        navigate(`/users/${defaultTab}`);
      }
    } else {
      // No ID provided - go to default tab
      const defaultTab = getDefaultTabIndex(role);
      navigate(`/users/${defaultTab}`);
    }
  }, [id, navigate, role]);

  // Security check - prevent rendering if user has no authorized tabs
  if (authorizedTabs.length === 0) {
    return (
      <EmptyState
        icon={<LockOutlinedIcon />}
        title={t('no_access_permission', { defaultValue: 'You do not have permission to access this section.' })}
      />
    );
  }

  const tabs = [
    { key: 'riders', index: TAB_CONFIG.RIDERS.index, label: t('riders'), component: <Riders /> },
    { key: 'drivers', index: TAB_CONFIG.DRIVERS.index, label: t('drivers'), component: <Drivers /> },
    { key: 'fleetadmins', index: TAB_CONFIG.FLEET_ADMINS.index, label: t('fleetadmins'), component: <FleetAdmins /> },
    { key: 'internal_staff', index: TAB_CONFIG.INTERNAL_STAFF.index, label: t('internal_staff', { defaultValue: 'Internal Staff' }), component: <InternalStaff /> },
    { key: 'alladmin', index: TAB_CONFIG.CREATE_ADMIN.index, label: t('alladmin'), component: <CreateAdmin /> },
  ].filter((tab) => canAccessTab(tab.index, role));
  // Only render the tab panel the user has access to (and only the open one).
  const current = tabs.find((tab) => tab.index === value);

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%' }}>
      <Tabs
        value={current ? value : false}
        onChange={handleChange}
        aria-label={calmCaps(t('users'))}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: `1px solid ${c.OUTLINE}` }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} value={tab.index} label={calmCaps(tab.label)} id={`users-tab-${tab.index}`} aria-controls={`users-tabpanel-${tab.index}`} />
        ))}
      </Tabs>
      {current ? (
        <Box role="tabpanel" id={`users-tabpanel-${current.index}`} aria-labelledby={`users-tab-${current.index}`} sx={{ pt: 2.5 }}>
          {current.component}
        </Box>
      ) : null}
    </Box>
  );
}
